import { useState } from 'react';
import { Segmented } from '@hana/ui';
import { useT } from '@hana/i18n';
import { KPI_KINDS, KPI_METRICS, KPI_PENALTY, type KpiKind } from './kpiRules';
import { fi } from './kpiPeople';
import d from './kpiDept.module.css';
import s from './kpiRules.module.css';

// Mint-KPI · tab Quy tắc KPI (code board: tab === 'rule'). Danh mục định nghĩa + luật tính thưởng. Chỉ đọc; không tiền (mã chỉ số tiền ẩn).

const MCOL = ['13%', '35%', '12%', '9%', '10%', '10%', '11%'];
const RCOL = ['14%', '9%', '9%', '18%', '16%', '14%', '20%'];

export function useRuleSegs() {
  const t = useT();
  const [kind, setKind] = useState<KpiKind>('Metric');
  const segs = <Segmented kind="toggle" label={t('hr.kr.kindLabel')} value={kind} onChange={setKind} items={KPI_KINDS.map((k) => ({ key: k.key, label: k.label }))} />;
  return { kind, segs };
}

function Tile({ k, v, sub, warn }: { k: string; v: string; sub?: string; warn?: boolean }) {
  return <div className={d.tile}><span>{k}</span><b style={warn ? { color: 'var(--warn)' } : undefined}>{v}</b>{sub ? <span>{sub}</span> : null}</div>;
}

function Rows({ rows }: { rows: [string, string, string?][] }) {
  return (
    <div className={d.list}>
      {rows.map(([k, v, warn]) => (
        <div key={k} className={d.lrow}>
          <span>{k}</span><span>{warn ? '' : v}</span>
          {warn ? <b style={{ color: 'var(--warn)' }}>{v}</b> : null}
        </div>
      ))}
    </div>
  );
}

function Note({ text }: { text: string }) {
  return <div className={s.note}>{text}</div>;
}

export function HrKpiRules({ kind, onNote }: { kind: KpiKind; onNote: (m: string) => void }) {
  const t = useT();
  const add = () => onNote(t('hr.noWrite'));
  const pending = t('hr.kr.pending');
  const def = (() => {
    if (kind === 'Metric') {
      return (
        <section className={d.panel} aria-label={t('hr.kr.metricKind')}>
          <div className={d.hd}><h2>{t('hr.kr.metricKind')}</h2><span>{t('hr.kr.metricSub', { n: KPI_METRICS.length })}</span></div>
          <table className={d.tbl} aria-label={t('hr.kr.metricKind')}>
            <colgroup>{MCOL.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
            <thead><tr><th scope="col">{t('hr.kr.thCode')}</th><th scope="col">{t('hr.kr.thName')}</th><th scope="col">{t('hr.kr.thDept')}</th><th scope="col">{t('hr.kr.thUnit')}</th><th scope="col">{t('hr.kr.thDir')}</th><th scope="col">{t('hr.kr.thSource')}</th><th scope="col">{t('hr.kr.thWeight')}</th></tr></thead>
            <tbody>
              {KPI_METRICS.map(([m, dept, name, unit, dir, src]) => (
                <tr key={m}>
                  <td className={d.sc}>{m}</td><td>{name}</td><td className={d.seat}>{dept}</td><td className={d.seat}>{unit}</td><td className={d.seat}>{dir}</td><td className={d.none}>{src}</td>
                  <td style={{ color: 'var(--warn)' }}>{pending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      );
    }
    if (kind === 'Penalty') {
      return <section className={d.panel} aria-label={t('hr.kr.penKind')}><div className={d.hd}><h2>{t('hr.kr.penKind')}</h2><span>{t('hr.kr.penSub')}</span></div><Rows rows={KPI_PENALTY} /></section>;
    }
    if (kind === 'Reward') {
      return <section className={d.panel} aria-label={t('hr.kr.rewKind')}><div className={d.hd}><h2>{t('hr.kr.rewKind')}</h2><span>{t('hr.kr.rewSub')}</span></div><Rows rows={[['R_PRAISE', t('hr.kr.rPraise')], ['R_RECOVER', t('hr.kr.rRecover')], ['R_SAVE', t('hr.kr.rSave')], [t('hr.kr.rOther'), t('hr.kr.rUnmapped')]]} /></section>;
    }
    if (kind === 'Severity') {
      return <section className={d.panel} aria-label={t('hr.kr.sevKind')}><div className={d.hd}><h2>{t('hr.kr.sevKind')}</h2><span>{t('hr.kr.sevSub')}</span></div><Rows rows={[['L1', t('hr.kr.l1')], ['L2', t('hr.kr.l2')], ['L3', t('hr.kr.l3')], ['L4', t('hr.kr.l4')], [t('hr.kr.sevPenalty'), pending, 'w']]} /></section>;
    }
    const unit = kind === 'Unit';
    return (
      <section className={d.panel} aria-label={unit ? t('hr.kr.unitKind') : t('hr.kr.paramKind')}>
        <div className={d.hd}><h2>{unit ? t('hr.kr.unitKind') : t('hr.kr.paramKind')}</h2><span>{t('hr.kr.rows', { n: unit ? '31' : '22' })}</span></div>
        <Note text={unit ? t('hr.kr.unitNote') : t('hr.kr.paramNote')} />
      </section>
    );
  })();
  return (
    <div className={d.grid}>
      <section className={`${d.tiles} ${s.tiles6}`} aria-label={t('hr.kr.metrics')}>
        <Tile k="TblKPIDef" v="107" sub={t('hr.kr.defParam')} />
        <Tile k={t('hr.kr.metrics')} v="36" />
        <Tile k={t('hr.kr.unitPts')} v="31" />
        <Tile k={t('hr.kr.paramKind')} v="22" />
        <Tile k={t('hr.kr.penRewSev')} v="9 · 5 · 4" />
        <Tile k="TblKPIRule" v={fi(0)} sub={t('hr.kr.noData')} warn />
      </section>
      {def}
      <section className={d.panel} aria-label={t('hr.kr.rules')}>
        <div className={d.hd}><h2>{t('hr.kr.rules')}</h2><span>{t('hr.kr.rulesSub')}</span><button type="button" onClick={add}>{t('hr.kpi.addRule')}</button></div>
        <table className={d.tbl} aria-label={t('hr.kr.rules')}>
          <colgroup>{RCOL.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <thead><tr><th scope="col">{t('hr.kr.seat')}</th><th scope="col">{t('hr.kr.grade')}</th><th scope="col">{t('hr.kr.period')}</th><th scope="col">{t('hr.kr.thSource')}</th><th scope="col">{t('hr.kr.field')}</th><th scope="col">{t('hr.kr.how')}</th><th scope="col">{t('hr.kr.effFrom')}</th></tr></thead>
          <tbody><tr><td colSpan={7} className={d.none}>{t('hr.kr.noRules')}</td></tr></tbody>
        </table>
      </section>
    </div>
  );
}
